/**
 * `g` then a letter: go to a destination.
 *
 * The chord every console an operator already knows uses (GitHub, Linear,
 * Gmail). Two keys rather than one so a stray letter typed with focus on the
 * page does nothing, and a pure function rather than a hook so the decisions —
 * which letter, how long the `g` stays armed, what cancels it — are testable
 * without a browser. The listener in `App.tsx` has no decisions in it.
 */

import type { ViewKey } from './route.ts';

/** How long after `g` the second key still counts. */
export const CHORD_WINDOW_MS = 1500;

export const GO_KEYS: Readonly<Record<string, ViewKey>> = {
  f: 'fleet',
  a: 'alerts',
  s: 'sites',
  u: 'uptime',
  o: 'footage',
  i: 'sim',
  y: 'activity',
  p: 'power',
  e: 'settings',
};

export interface ChordState {
  /** When `g` was pressed, or null when nothing is armed. */
  armedAt: number | null;
}

export interface KeyInput {
  key: string;
  at: number;
  /** A modifier was held: never part of a chord. */
  modified: boolean;
  /** Focus is in something a person types into. */
  typing: boolean;
}

export function readChord(state: ChordState, input: KeyInput): { state: ChordState; go: ViewKey | null } {
  const disarmed: ChordState = { armedAt: null };
  if (input.modified || input.typing) return { state: disarmed, go: null };

  const key = input.key.toLowerCase();
  const armed = state.armedAt !== null && input.at - state.armedAt <= CHORD_WINDOW_MS;

  if (armed) {
    const view = GO_KEYS[key];
    if (view !== undefined) return { state: disarmed, go: view };
    // `g g` re-arms rather than cancelling, so a double-tap is not a dead end.
    return { state: key === 'g' ? { armedAt: input.at } : disarmed, go: null };
  }
  return { state: key === 'g' ? { armedAt: input.at } : disarmed, go: null };
}
